// YieldHistory — past distributions + my receipts + countdown to next one.
import React, { useEffect, useState } from "react";
import { ExternalLink, Clock } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tVault } from "../../i18n/vault";

const fmt = (n) => Number(n).toLocaleString();

function useCountdown(targetIso) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    if (!targetIso) return;
    const update = () => {
      const ms = new Date(targetIso).getTime() - Date.now();
      if (ms <= 0) { setTxt("—"); return; }
      const d = Math.floor(ms / 86_400_000);
      const h = Math.floor((ms % 86_400_000) / 3_600_000);
      setTxt(`${d}d ${h}h`);
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [targetIso]);
  return txt;
}

export default function YieldHistory({ distributions, nextDistribution, myReceipts }) {
  const { lang } = useLanguage();
  const t = (k) => tVault(lang, k);
  const countdown = useCountdown(nextDistribution);
  const mineByPeriod = Object.fromEntries((myReceipts || []).map((r) => [r.period, r]));

  return (
    <div data-testid="vault-yield-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
          {t("yieldHistory")}
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/[0.08] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-300" data-testid="vault-countdown">
          <Clock className="h-3 w-3" /> {t("nextDistribution")}: {countdown || "…"}
        </span>
      </div>

      {!distributions?.length ? (
        <p className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-xs text-white/40" data-testid="vault-history-empty">
          {t("empty")}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                <th className="px-4 py-3">{t("period")}</th>
                <th className="px-4 py-3">{t("totalYield")}</th>
                <th className="px-4 py-3">{t("depositors")}</th>
                <th className="px-4 py-3">{t("myShare")}</th>
                <th className="px-4 py-3">{t("viewTx")}</th>
              </tr>
            </thead>
            <tbody>
              {distributions.map((d) => {
                const mine = mineByPeriod[d.period];
                return (
                  <tr key={d.period} className="border-b border-white/5 text-white/75">
                    <td className="px-4 py-3 font-mono">{d.period}</td>
                    <td className="px-4 py-3 font-mono text-amber-300">
                      {fmt(d.total_yield_rbl)} R₿L
                      {d.total_yield_usd != null && <span className="ml-1 text-white/35">≈ ${fmt(d.total_yield_usd)}</span>}
                    </td>
                    <td className="px-4 py-3">{d.depositor_count}</td>
                    <td className="px-4 py-3 font-mono">
                      {mine ? <span className="text-emerald-300">+{fmt(mine.amount_rbl)} R₿L</span> : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {mine?.tx_hash ? (
                        <a href={`https://basescan.org/tx/${mine.tx_hash}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-300 underline">
                          <ExternalLink className="h-3 w-3" /> {t("viewTx")}
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
